import { memo, useState, useCallback } from 'react'
import {
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  useColorModeValue,
  Button,
  Heading,
  HStack,
  VStack,
  Box,
  Text,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import {
  LuSearch,
  LuDownload,
  LuChevronLeft,
  LuFolder,
  LuTrash2,
  LuCopy,
  LuPencil,
} from 'react-icons/lu'
import { DeleteIcon } from '@chakra-ui/icons'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { ProjectSourcesSidebar } from './ProjectWorkspace/ProjectSourcesSidebar'
import { ChatView } from './ProjectWorkspace/ChatView'
import { ExplorationView } from './ProjectWorkspace/ExplorationView'
import { ExportView } from './ProjectWorkspace/ExportView'
import axiosClient from '../../../api/axios'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface ProjectWorkspaceProps {
  project: any
  onBack: () => void
}

export const ProjectWorkspace = memo(function ProjectWorkspace({
  project,
  onBack,
}: ProjectWorkspaceProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [clearHistoryKey, setClearHistoryKey] = useState(0)
  const [projectData, setProjectData] = useState(project)

  // Modals state
  const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isDuplicateOpen, onOpen: onDuplicateOpen, onClose: onDuplicateClose } = useDisclosure()

  const [renameForm, setRenameForm] = useState({ title: project.title, description: project.description || '' })
  const [duplicateForm, setDuplicateForm] = useState({ title: `${project.title} (copie)`, description: project.description || '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const sidebarBg = useColorModeValue('gray.50', 'gray.800')

  const handleSourceSelect = useCallback((sourceId: string) => {
    setSelectedSourceId(sourceId)
    // Optionally switch to exploration tab when a source is selected
    // setSelectedTab(1)
  }, [])

  const handleClearHistory = useCallback(() => {
    const storageKey = `aletheia_chat_project_${project.id}`
    try {
      localStorage.removeItem(storageKey)
      // Force re-render of ChatView by changing key
      setClearHistoryKey(prev => prev + 1)
    } catch (error) {
      console.error('Failed to clear chat history:', error)
    }
  }, [project.id])

  const handleRenameProject = useCallback(async () => {
    if (!renameForm.title.trim()) {
      toast({
        title: 'Titre requis',
        description: 'Veuillez entrer un titre pour le projet',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    setIsSubmitting(true)
    try {
      await axiosClient.put(`medinsight/projects/${projectData.id}`, {
        title: renameForm.title.trim(),
        description: renameForm.description.trim(),
      })

      setProjectData({ ...projectData, title: renameForm.title.trim(), description: renameForm.description.trim() })

      toast({
        title: 'Projet renommé',
        status: 'success',
        duration: 3000,
      })
      onRenameClose()
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de renommer le projet',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [renameForm, projectData, toast, onRenameClose])

  const handleDeleteProject = useCallback(async () => {
    setIsSubmitting(true)
    try {
      await axiosClient.delete(`medinsight/projects/${projectData.id}`)

      toast({
        title: 'Projet supprimé',
        status: 'success',
        duration: 3000,
      })
      onDeleteClose()
      onBack()
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de supprimer le projet',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [projectData, toast, onDeleteClose, onBack])

  const handleDuplicateProject = useCallback(async () => {
    if (!duplicateForm.title.trim()) {
      toast({
        title: 'Titre requis',
        description: 'Veuillez entrer un titre pour le nouveau projet',
        status: 'warning',
        duration: 3000,
      })
      return
    }

    setIsSubmitting(true)
    try {
      await axiosClient.post('medinsight/projects', {
        title: duplicateForm.title.trim(),
        description: duplicateForm.description.trim(),
        specialty: projectData.specialty,
        type: projectData.type,
        color: projectData.color,
      })

      toast({
        title: 'Projet dupliqué',
        description: 'Le nouveau projet a été créé avec succès',
        status: 'success',
        duration: 3000,
      })
      onDuplicateClose()
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de dupliquer le projet',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [duplicateForm, projectData, toast, onDuplicateClose])

  return (
    <VStack spacing={4} align="stretch" px={{ base: 2, md: 4, lg: 6 }} pt={{ base: 4, md: 6 }}>
      {/* Gradient Header with back action */}
      <Box
        bgGradient="linear(135deg, brand.purple 0%, purple.500 50%, pink.400 100%)"
        borderRadius="3xl"
        p={{ base: 5, md: 6 }}
        boxShadow="2xl"
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)',
          borderRadius: '3xl',
          pointerEvents: 'none',
        }}
      >
        <HStack justify="space-between" align="center" position="relative" spacing={4}>
          <HStack spacing={4} align="center" flex={1}>
            <IconButton
              aria-label={t('medinsight.back')}
              icon={<Icon as={LuChevronLeft} />}
              variant="ghost"
              onClick={onBack}
              size="sm"
              color="white"
              colorScheme="whiteAlpha"
              _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-1px)' }}
            />
            <Box p={3} bg="whiteAlpha.300" backdropFilter="blur(10px)" borderRadius="xl">
              <Icon as={LuFolder} boxSize={8} color="white" />
            </Box>
            <VStack spacing={0} align="start">
              <Heading as="h1" size="lg" color="white">
                {projectData.title}
              </Heading>
              <Text fontSize="sm" color="whiteAlpha.900">
                {t('medinsight.projectSubtitle', {
                  count: projectData.articleCount || 0,
                })}
              </Text>
            </VStack>
          </HStack>

          {/* Three-dot menu */}
          <Menu>
            <Tooltip label="Options du projet">
              <MenuButton
                as={IconButton}
                icon={<Icon as={BsThreeDotsVertical} />}
                variant="ghost"
                color="white"
                colorScheme="whiteAlpha"
                size="md"
                borderRadius="lg"
                _hover={{ bg: 'whiteAlpha.200' }}
                aria-label="Options du projet"
              />
            </Tooltip>
            <MenuList>
              <MenuItem
                icon={<Icon as={LuPencil} />}
                onClick={() => {
                  setRenameForm({ title: projectData.title, description: projectData.description || '' })
                  onRenameOpen()
                }}
              >
                Renommer le projet
              </MenuItem>
              <MenuItem
                icon={<Icon as={LuCopy} />}
                onClick={() => {
                  setDuplicateForm({ title: `${projectData.title} (copie)`, description: projectData.description || '' })
                  onDuplicateOpen()
                }}
              >
                Dupliquer le projet
              </MenuItem>
              <MenuDivider />
              <MenuItem
                icon={<Icon as={LuTrash2} />}
                color="red.500"
                onClick={onDeleteOpen}
              >
                Supprimer le projet
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Box>

      {/* Main Workspace */}
      <Grid
        w="100%"
        templateColumns={{
          base: '1fr',
          lg: '300px 1fr 360px',
          xl: '320px 1fr 380px',
        }}
        gap={{ base: 4, lg: 5 }}
        alignItems="start"
      >
          {/* Left Sidebar - Sources */}
          <GridItem
            position={{ base: 'relative', lg: 'sticky' }}
            top={{ base: undefined, lg: '12px' }}
            alignSelf="start"
            maxH={{ base: 'auto', lg: 'calc(100vh - 180px)' }}
            overflow="hidden"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            bg={sidebarBg}
            boxShadow="sm"
            transition="all 0.2s"
            _hover={{
              boxShadow: 'md',
            }}
          >
            <VStack align="stretch" spacing={3} h="full" p={5}>
              <HStack justify="space-between" align="center">
                <HStack spacing={2}>
                  <Box p={2} bg="purple.50" borderRadius="lg">
                    <Icon as={LuFolder} color="purple.600" boxSize={4} />
                  </Box>
                  <Heading size="sm" fontWeight="semibold">{t('medinsight.sources.title')}</Heading>
                </HStack>
              </HStack>
              <Box flex={1} overflowY="auto">
                <ProjectSourcesSidebar
                  projectId={project.id}
                  onSourceSelect={handleSourceSelect}
                  selectedSourceId={selectedSourceId}
                />
              </Box>
            </VStack>
          </GridItem>

          {/* Center - Aletheia Chat (Featured) */}
          <GridItem
            overflow="hidden"
            h="calc(100vh - 180px)"
            maxH="calc(100vh - 180px)"
            borderRadius="2xl"
            position="relative"
            boxShadow="xl"
            transition="all 0.3s"
            _hover={{
              boxShadow: '2xl',
            }}
          >
            {/* Aletheia Premium Header */}
            <Box
              bgGradient="linear(135deg, purple.500 0%, pink.400 100%)"
              p={4}
              h="72px"
              flexShrink={0}
              position="relative"
              overflow="hidden"
              display="flex"
              alignItems="center"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)',
                pointerEvents: 'none',
              }}
            >
              <HStack justify="space-between" position="relative" zIndex={1} w="full">
                <HStack spacing={3}>
                  <Box
                    p={2.5}
                    bg="whiteAlpha.300"
                    backdropFilter="blur(10px)"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="whiteAlpha.400"
                  >
                    <Text fontSize="2xl" fontWeight="bold" fontFamily="Georgia, serif" color="white">
                      Λ
                    </Text>
                  </Box>
                  <VStack spacing={0} align="start">
                    <Heading size="md" color="white" fontWeight="bold">
                      Aletheia
                    </Heading>
                    <Text fontSize="xs" color="whiteAlpha.900" fontWeight="medium">
                      {projectData.title}
                    </Text>
                  </VStack>
                </HStack>
                <Tooltip label="Effacer l'historique">
                  <IconButton
                    aria-label="Effacer l'historique"
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    borderRadius="lg"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    onClick={handleClearHistory}
                  />
                </Tooltip>
              </HStack>
            </Box>

            {/* Chat Content */}
            <Box bg="white" h="calc(100% - 72px)">
              <ChatView
                key={clearHistoryKey}
                projectId={project.id}
                projectName={project.title}
                articleCount={project.articleCount}
              />
            </Box>
          </GridItem>

          {/* Right - Exploration & Export */}
          <GridItem
            overflow="hidden"
            bg="white"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            h="calc(100vh - 180px)"
            maxH="calc(100vh - 180px)"
            transition="all 0.2s"
            _hover={{
              boxShadow: 'md',
            }}
          >
            <Tabs
              index={selectedTab}
              onChange={setSelectedTab}
              h="full"
              display="flex"
              flexDirection="column"
              variant="soft-rounded"
              colorScheme="purple"
            >
              <Box px={5} pt={4} pb={2} flexShrink={0}>
                <Heading size="sm" mb={1} fontWeight="semibold">{t('medinsight.exploration.title')}</Heading>
                <Text fontSize="xs" color="gray.500">
                  {t('medinsight.exploration.subtitle')}
                </Text>
              </Box>
              <TabList px={5} pt={2} pb={3} flexShrink={0}>
                <Tab fontSize="sm" fontWeight="medium">
                  <Icon as={LuSearch} mr={2} boxSize={4} />
                  {t('medinsight.exploration.tab')}
                </Tab>
                <Tab fontSize="sm" fontWeight="medium">
                  <Icon as={LuDownload} mr={2} boxSize={4} />
                  {t('medinsight.export.tab')}
                </Tab>
              </TabList>

              <TabPanels flex={1} overflow="hidden">
                <TabPanel h="full" p={0} pt={2}>
                  <ExplorationView
                    projectId={project.id}
                    selectedSourceId={selectedSourceId}
                  />
                </TabPanel>

                <TabPanel h="full" p={0} pt={2}>
                  <ExportView projectId={projectData.id} projectTitle={projectData.title} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </GridItem>
      </Grid>

      {/* Rename Project Modal */}
      <Modal isOpen={isRenameOpen} onClose={onRenameClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Renommer le projet</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Titre</FormLabel>
                <Input
                  value={renameForm.title}
                  onChange={(e) => setRenameForm({ ...renameForm, title: e.target.value })}
                  placeholder="Titre du projet"
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={renameForm.description}
                  onChange={(e) => setRenameForm({ ...renameForm, description: e.target.value })}
                  placeholder="Description du projet (optionnel)"
                  rows={4}
                  borderRadius="lg"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRenameClose} borderRadius="lg">
              Annuler
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleRenameProject}
              isLoading={isSubmitting}
              borderRadius="lg"
            >
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Project Alert Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Supprimer le projet
            </AlertDialogHeader>

            <AlertDialogBody>
              Êtes-vous sûr de vouloir supprimer le projet "<strong>{projectData.title}</strong>" ?
              Cette action est irréversible et supprimera toutes les sources et données associées.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose} borderRadius="lg">
                Annuler
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteProject}
                ml={3}
                isLoading={isSubmitting}
                borderRadius="lg"
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Duplicate Project Modal */}
      <Modal isOpen={isDuplicateOpen} onClose={onDuplicateClose} size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader>Dupliquer le projet</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Projet source:
                </Text>
                <Text fontWeight="semibold">{projectData.title}</Text>
              </Box>
              <FormControl isRequired>
                <FormLabel>Titre du nouveau projet</FormLabel>
                <Input
                  value={duplicateForm.title}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, title: e.target.value })}
                  placeholder="Titre du nouveau projet"
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={duplicateForm.description}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, description: e.target.value })}
                  placeholder="Description du projet (optionnel)"
                  rows={4}
                  borderRadius="lg"
                />
              </FormControl>
              <Text fontSize="xs" color="gray.500">
                Note: Seules les métadonnées du projet seront copiées. Les sources et articles ne seront pas dupliqués.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDuplicateClose} borderRadius="lg">
              Annuler
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleDuplicateProject}
              isLoading={isSubmitting}
              borderRadius="lg"
            >
              Dupliquer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  )
})
